/**
 * The slice player. Slices are WINDOWS onto the one decoded buffer (REX-style:
 * no per-slice copies), retriggered at grid times.
 *
 * Transferred from Chirp/shrike.html `playSlice` — including its short
 * fade-out, so a long tail never clicks when the next hit cuts it. Transient
 * detection is deliberately NOT transferred: this scene uses a FIXED division
 * of the bar, which is legible on a phone and needs no sensitivity slider.
 */

export interface Slice {
  /** Sample offset into the buffer. */
  start: number;
  /** Length in samples. */
  length: number;
}

/** Fixed division of a region into n equal slices. Pure sample arithmetic. */
export function fixedSlices(totalSamples: number, n: number, offset = 0): Slice[] {
  const out: Slice[] = [];
  const span = Math.max(1, Math.floor(totalSamples / n));
  for (let i = 0; i < n; i++) {
    const start = offset + i * span;
    out.push({ start, length: i === n - 1 ? Math.max(1, totalSamples - i * span) : span });
  }
  return out;
}

export interface BreakPlayer {
  play(sliceIdx: number, when: number, vel?: number, rate?: number): void;
  setLevel(v: number): void;
  readonly slices: Slice[];
  dispose(): void;
}

export function createBreakPlayer(
  ctx: BaseAudioContext,
  out: AudioNode,
  buffer: AudioBuffer,
  sliceCount: number,
  level = 0.85,
): BreakPlayer {
  const master = ctx.createGain();
  master.gain.value = level * level;
  master.connect(out);
  const slices = fixedSlices(buffer.length, sliceCount);
  const sr = buffer.sampleRate;

  return {
    slices,
    play(idx, when, vel = 0.95, rate = 1) {
      const s = slices[((idx % slices.length) + slices.length) % slices.length];
      if (!s) return;
      const off = s.start / sr;
      const dur = Math.max(0.03, s.length / sr / Math.max(0.05, rate));
      const src = ctx.createBufferSource();
      src.buffer = buffer;
      src.playbackRate.value = rate;
      const g = ctx.createGain();
      g.gain.setValueAtTime(Math.max(0.0001, vel), when);
      g.gain.setValueAtTime(Math.max(0.0001, vel), when + Math.max(0, dur - 0.012));
      g.gain.linearRampToValueAtTime(0.0001, when + dur);
      src.connect(g); g.connect(master);
      src.start(when, off, dur * rate + 0.02);
      src.stop(when + dur + 0.03);
      src.onended = () => { try { g.disconnect(); } catch { /* gone */ } };
    },
    setLevel(v) { master.gain.setTargetAtTime(v * v, ctx.currentTime, 0.02); },
    dispose() {
      master.gain.setTargetAtTime(0.0001, ctx.currentTime, 0.02); // no click on scene switch
      setTimeout(() => { try { master.disconnect(); } catch { /* gone */ } }, 120);
    },
  };
}
